import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useStateValue } from '../context/StateProvider';
import { firestore, storage } from '../firebase.config';
import { FaTrashAlt, FaFileDownload } from 'react-icons/fa';
import { Helmet } from 'react-helmet';

const MyAccount = () => {
  const [{ user }] = useStateValue();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [photoURL, setPhotoURL] = useState('');
  const [mobile, setMobile] = useState('');
  const [resume, setResume] = useState(null);
  const [resumeURL, setResumeURL] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchUserData = async () => {
      if (user && user.username) { // Check if user and username are defined
        const userDocRef = doc(firestore, 'users', user.username); // Use username
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
            const userData = userDoc.data();
            setName(userData.username || user.username || '');
            setEmail(userData.email || user.email || '');
            setMobile(userData.mobile || user.mobile || '');
            setResumeURL(userData.resume || '');
            setPhotoURL(userData.photoURL || user.photoURL || '');
        }
    }
};

    fetchUserData();
  }, [user]);

  const handleResumeChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setResume(file);
    }
  };

  const handleUpdate = async () => {
    setLoading(true);
    try {
      const userDocRef = doc(firestore, 'users', user.username); 
      let resumeUrl = resumeURL;

      if (resume) {
        const resumeRef = ref(storage, `resumes/${resume.name}`);
        await uploadBytes(resumeRef, resume);
        resumeUrl = await getDownloadURL(resumeRef);
      }

      await updateDoc(userDocRef, {
        name,
        email,
        photoURL,
        mobile,
        resume: resumeUrl,
      });
      setMessage('Details updated successfully!');
      setResumeURL(resumeUrl);
      window.location.reload();
    } catch (error) {
      console.error('Error updating details: ', error);
      setMessage('Failed to update details. Try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteResume = async () => {
    const userDocRef = doc(firestore, 'users', user.username);
    const isConfirmed = window.confirm("Are you sure you want to delete your resume?");
  
    if (isConfirmed) {
      try {
        await updateDoc(userDocRef, {
          resume: '',
        });
        setResumeURL('');
        setMessage('Resume deleted successfully!');
        window.location.reload();
      } catch (error) {
        console.error('Error deleting resume: ', error);
        setMessage('Failed to delete resume. Try again later.');
      }
    } else {
      setMessage('Resume deletion cancelled.');
    }
  };
  
  return (
    <div className="flex justify-center items-center min-h-screen">
      <Helmet>
        <title>My Account </title>
        <meta name="description" content={`Manage your account details for account. Update profile information and resume.`} />
      </Helmet>
      {/* Profile Section */}
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        <div className="flex flex-col items-center mb-6">
          {photoURL && (
            <img
              src={photoURL}
              alt="User profile"
              className="rounded-full w-24 h-24 object-cover border-2 border-[#143749]"
            />
          )}
           <h2 className="text-3xl font-bold mb-2 text-center text-[#143749]">
          My Info
        </h2>

          <p className="text-xl font-semibold text-center text-[#005eb8]">
            Hello, {name}
          </p>
        </div>
       
        <div className="mb-4">
          <label className="block mb-1 text-xs font-semibold text-gray-800">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full pl-2 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-colors"
            placeholder="Enter your name"
          />
        </div>

        <div className="mb-4">
          <label className="block mb-1 text-xs font-semibold text-gray-800">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled
            className="w-full pl-2 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-colors"
            placeholder="Email address"
          />
        </div>

        <div className="mb-4">
          <label className="block mb-1 text-xs font-semibold text-gray-800">Mobile Number (with country code)</label>
          <input
            type="text"
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            className="w-full pl-2 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-colors"
            placeholder="Enter mobile number"
          />
        </div>

        {!resumeURL && (
          <div className="mb-4">
            <label className="block mb-1 text-xs font-semibold text-gray-800">Upload Resume</label>
            <div className="flex items-center">
              <label
                htmlFor="file-upload"
                className="cursor-pointer bg-[#005eb8] text-white py-2 px-4 text-center text-[13px]"
              >
                Choose File
              </label>
              <input
                id="file-upload"
                type="file"
                accept=".pdf, .doc, .docx"
                onChange={handleResumeChange}
                className="hidden"
              />
              <span className="ml-4 text-[13px] text-gray-600">
                {resume ? resume.name : 'No file chosen'}
              </span>
            </div>
          </div>
        )}

        {resumeURL && (
          <div className="mb-4 flex items-center gap-2">
            <p className="font-medium mb-2 flex items-center text-[13px]">Your Resume:</p>
            <div className="flex items-center gap-1">
              <FaFileDownload className="ml-2 text-[#143749] text-[13px]" />
              <a href={resumeURL} target="_blank" rel="noopener noreferrer" className="text-[#143749] text-semibold underline text-[13px]">
                View Resume
              </a>
            </div>
            <button onClick={handleDeleteResume} className="text-red-600 underline ml-4 flex items-center text-[13px]">
              <FaTrashAlt className="mr-1" />
              Delete Resume
            </button>
          </div>
        )}

        {message && <p className="text-green-500 mb-4 text-[13px]">{message}</p>}

        <button onClick={handleUpdate} disabled={loading} className="w-full bg-gradient-to-tr bg-[#005eb8] text-white py-2 px-4">
          {loading ? 'Updating...' : 'Update Details'}
        </button>
      </div>
    </div>
  );
};

export default MyAccount;
